import React, {useCallback, useState} from 'react';

import {cls} from '../utils';
import {useGallery} from './gallery-context';
import type {GalleryImage} from './gallery-context';
import {GalleryImageToolbar} from './gallery-image-toolbar';
import type {GalleryTarget} from './gallery-upload';
import {useGalleryAdmin} from './use-gallery-admin';

import css from './gallery.module.css';

type Props = {
	target: GalleryTarget;
	/** все картинки сущности в текущем порядке: порядок меняется для всей галереи, а не только для показанных */
	allImages: GalleryImage[];
	/** какие из них показаны (у подкласса и предмета первая стоит отдельно в шапке) */
	shownImages: GalleryImage[];
};

export const GalleryAdminList: React.FC<Props> = ({target, allImages, shownImages}) => {
	const openImage = useGallery();
	const {error, move, shift, edit, requestDelete, dialogs} = useGalleryAdmin(target, allImages);

	const [dragged, setDragged] = useState<string>();
	const [over, setOver] = useState<string>();

	const handleDrop = useCallback(
		(targetId: string) => {
			const source = dragged;
			setDragged(undefined);
			setOver(undefined);
			if (!source || source === targetId) return;
			move(
				source,
				allImages.findIndex((image) => image.id === targetId),
			);
		},
		[dragged, allImages, move],
	);

	return (
		<>
			{dialogs}
			{error && <div className={css.uploadError}>{error}</div>}
			<ul className={css.adminList}>
				{shownImages.map((image, index) => {
					const position = allImages.findIndex((item) => item.id === image.id);
					return (
						<li
							key={image.id}
							className={cls(
								css.adminItem,
								dragged === image.id && css.dragged,
								over === image.id && dragged !== image.id && css.dropTarget,
							)}
							onDragOver={(event) => {
								if (!dragged) return;
								event.preventDefault();
								setOver(image.id);
							}}
							onDragLeave={() => setOver((current) => (current === image.id ? undefined : current))}
							onDrop={(event) => {
								event.preventDefault();
								handleDrop(image.id);
							}}
						>
							<a
								href={image.url}
								className={css.adminThumb}
								title={image.description || `Иллюстрация ${index + 1}`}
								onClick={(event) => {
									event.preventDefault();
									openImage(image.id);
								}}
							>
								<img src={image.preview} alt={image.description || `Иллюстрация ${index + 1}`} loading="lazy" decoding="async" />
							</a>
							<GalleryImageToolbar
								dragHandle={{
									onDragStart: (event) => {
										event.dataTransfer.effectAllowed = 'move';
										event.dataTransfer.setData('text/plain', image.id);
										setDragged(image.id);
									},
									onDragEnd: () => {
										setDragged(undefined);
										setOver(undefined);
									},
								}}
								onMoveLeft={position > 0 ? () => shift(image.id, -1) : undefined}
								onMoveRight={position < allImages.length - 1 ? () => shift(image.id, 1) : undefined}
								onEdit={() => edit(image)}
								onDelete={() => requestDelete(image.id)}
							/>
						</li>
					);
				})}
			</ul>
		</>
	);
};
